<template>
  <div>
    <div class="page-head">
      <h1>DSA Pass</h1>
      <p>One Pass unlocks 6 exam attempts across any Concentration Subjects, valid for 3 years.</p>
    </div>

    <dsa-loading v-if="loading" text="Loading Pass…" />
    <template v-else>
      <div v-if="hasActivePass" class="notice">
        <b>Active Pass:</b>
        {{ wallet.remaining_quota || 0 }} of {{ wallet.total_quota || 6 }} attempts remaining
        · valid until {{ formatExpire(wallet.expire_at) }}
      </div>
      <div v-for="n in notices" :key="n.code" class="notice" :class="{ warn: n.type === 'warning' }">
        {{ n.message }}
      </div>

      <div class="section">
        <div class="section-head"><h2>Purchase</h2></div>
        <div class="cta-card">
          <div>
            <h2>{{ productName }}</h2>
            <div class="feat">
              <span>6 exam attempts</span>
              <span>3-year validity</span>
              <span>Repeated attempts allowed (max three attempts per subject)</span>
            </div>
            <div class="price">
              <span v-if="showListPrice" class="list-price">{{ pricing.currency }} {{ pricing.list_price }}</span>
              Your price <b>{{ pricing.currency }} {{ pricing.your_price }}</b><br>
              <span style="font-size:12.5px">{{ pricing.note }}</span>
            </div>
            <p v-if="!products.length" class="hint" style="margin-top:12px">
              No Pass product is configured in the catalog yet. Contact support if purchase fails.
            </p>
          </div>
          <div class="cta-right">
            <button
              type="button"
              class="btn btn-primary btn-big"
              :disabled="buying || hasActivePass"
              @click="buy"
            >
              {{ buyLabel }}
            </button>
            <p v-if="pendingOrder" class="hint" style="margin-top:10px;max-width:260px">
              You have an unpaid order. Continue to payment.
            </p>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-head"><h2>Terms and conditions</h2></div>
        <div class="card terms-body" v-html="termsHtml" />
      </div>

      <div class="section" v-if="wallet.passes && wallet.passes.length">
        <div class="section-head"><h2>Wallet</h2></div>
        <table>
          <thead>
            <tr>
              <th>Pass</th>
              <th>Quota</th>
              <th>Purchased</th>
              <th>Expires</th>
              <th>Paid</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in wallet.passes" :key="p.order_id">
              <td>{{ p.pass_name || 'DSA Pass' }}</td>
              <td>{{ p.remaining }} / {{ p.quota }} left</td>
              <td>{{ formatDate(p.pay_time) }}</td>
              <td>{{ formatExpire(p.expire_at) }}</td>
              <td>{{ p.currency || 'USD' }} {{ p.pay_amount != null ? p.pay_amount : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import {
  getPassWallet,
  getDsaPassProducts,
  createDsaPassOrder,
  getStudentPurchasdPasses,
} from '@/api/dsa'
import { getUserId } from '@/utils/store'
import { formatDate, formatExpire } from '@/utils/dsaFormat'
import { resolvePassTermsHtml } from '@/constants/dsaPassTerms'

export default {
  name: 'StudentPass',
  data() {
    return {
      loading: true,
      buying: false,
      wallet: {},
      products: [],
      pricing: {
        currency: 'USD',
        list_price: 239,
        your_price: 159,
        note: 'Base USD 239 · automatic region & school reduction applied',
      },
      pendingOrder: null,
      notices: [],
    }
  },
  computed: {
    hasActivePass() {
      return (this.wallet.remaining_quota || 0) > 0
    },
    productName() {
      return (this.products[0] && this.products[0].pass_name) || 'DSA Registration Pass'
    },
    buyLabel() {
      if (this.hasActivePass) return 'Pass already active'
      if (this.buying) return 'Processing…'
      if (this.pendingOrder) return `Pay now — ${this.pricing.currency} ${this.pricing.your_price}`
      return `Buy Pass — ${this.pricing.currency} ${this.pricing.your_price}`
    },
    termsHtml() {
      return resolvePassTermsHtml()
    },
    showListPrice() {
      const list = Number(this.pricing.list_price)
      const price = Number(this.pricing.your_price)
      return list > 0 && price > 0 && list > price
    },
  },
  mounted() {
    if (this.$route.query.paid === '1') {
      this.$router.replace({ path: '/student/dashboard', query: { paid: '1' } })
      return
    }
    this.load()
  },
  methods: {
    formatDate,
    formatExpire,
    async load() {
      const student_id = getUserId()
      try {
        const [wRes, pRes, oRes] = await Promise.all([
          getPassWallet(student_id),
          getDsaPassProducts(student_id),
          getStudentPurchasdPasses(student_id),
        ])
        if (wRes.data && wRes.data.code === 0) {
          this.wallet = wRes.data.data || {}
          this.notices = []
          if ((this.wallet.remaining_quota || 0) <= 1 && (this.wallet.total_quota || 0) > 0) {
            this.notices.push({
              type: 'warning',
              code: 'quota_low',
              message: `Only ${this.wallet.remaining_quota || 0} attempt(s) remaining.`,
            })
          }
        }
        if (pRes.data && pRes.data.code === 0) {
          const data = pRes.data.data || {}
          this.products = data.products || []
          if (data.pricing) this.pricing = data.pricing
          if (this.products[0]) {
            this.pricing = {
              ...this.pricing,
              your_price: this.products[0].your_price,
              list_price: this.products[0].list_price,
              currency: this.products[0].currency || this.pricing.currency,
            }
          }
        }
        if (oRes.data && oRes.data.code === 0) {
          const orders = oRes.data.data || []
          const unpaid = orders.find(o => o.status === '未支付' && o.order_channel === 'DSA-PASS')
          this.pendingOrder = unpaid || null
        }
      } catch (e) {
        this.$emit('toast', 'Failed to load Pass')
      } finally {
        this.loading = false
      }
    },
    async buy() {
      if (this.hasActivePass || this.buying) return
      this.buying = true
      const student_id = getUserId()
      try {
        if (this.pendingOrder) {
          const orderId = this.pendingOrder.id || this.pendingOrder._id
          this.$router.push({ path: '/student/pass/order', query: { order_id: orderId } })
          return
        }
        const res = await createDsaPassOrder(student_id, '6a87e0cb2e8963cade0bba67')
        if (!res.data || res.data.code !== 0) {
          this.$emit('toast', (res.data && res.data.msg) || 'Could not create order')
          return
        }
        const order = res.data.data
        if (order.status === '已支付') {
          this.$emit('toast', 'Pass activated')
          await this.load()
          return
        }
        const orderId = order.id || order._id
        this.$router.push({ path: '/student/pass/order', query: { order_id: orderId } })
      } catch (e) {
        this.$emit('toast', 'Purchase failed')
      } finally {
        this.buying = false
      }
    },
  },
}
</script>

<style scoped>
.list-price{display:inline-block;margin-right:8px;text-decoration:line-through;color:var(--muted)}
.terms-body{font-size:14px;line-height:1.6;color:var(--gray)}
.terms-body >>> ul{margin:8px 0 12px 18px;padding:0}
.terms-body >>> li{margin-bottom:6px}
.terms-body >>> a{color:var(--blue)}
</style>
